"use client";

import { useEffect, useRef } from "react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  fixedSize?: boolean;
  children: React.ReactNode;
}

export default function Modal({ isOpen, onClose, title, fixedSize, children }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Keep the latest onClose in a ref so the keydown effect can remain
  // mounted exactly once per open/close cycle without re-firing on every
  // parent render. Previously the handler was a useCallback with [onClose]
  // in its deps — when the parent passed an inline arrow, onClose was a new
  // reference on every render, the effect re-ran, and a queued setTimeout
  // stole focus back to the first focusable element mid-keystroke (making
  // typing into a textarea inside the modal impossible).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Keyboard handler: Escape closes, Tab traps focus inside the modal.
  // Mounted once per open/close cycle (deps: [isOpen] only).
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }

      if (e.key === "Tab" && contentRef.current) {
        const focusable = contentRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Auto-focus the first focusable element when the modal opens —
  // exactly ONCE per open transition, not on every parent render.
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (contentRef.current) {
        // If the user has already focused something inside the modal
        // (e.g. they're typing into a textarea), leave their focus alone.
        if (contentRef.current.contains(document.activeElement)) return;
        const focusable = contentRef.current.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        focusable?.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div ref={contentRef} className={`modal-content${fixedSize ? " modal-fixed-size" : ""}`} role="dialog" aria-modal="true">
        {title && (
          <div className="modal-header">
            <h2>{title}</h2>
            <button className="btn-ghost" onClick={onClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
